/**
 * Versioned, quota-tolerant localStorage primitives.
 *
 * Generalises the safeRead/safeWrite pattern from bookmarkedFixtures.js and
 * adds the two things every ad-hoc persistence site in this app was missing:
 *
 *   VERSIONING. Preferences outlive the code that wrote them. Without an
 *   envelope, changing a stored shape silently feeds the old shape to new code,
 *   and the failure lands on a returning user weeks later — the hardest kind to
 *   reproduce. Every failure path here returns undefined so the caller falls
 *   back to its defaults, rather than half-applying a shape.
 *
 *   QUOTA. The gridState_* key space grows with (grid x competition x season)
 *   and is never garbage collected, so it is the realistic offender. A quota
 *   failure must degrade persistence, never break the app.
 *
 * Storage is a CACHE OF PREFERENCES, never a source of truth. Nothing here
 * should hold data the server owns.
 */

export const PERSIST_CHANGE_EVENT = 'aether-persisted-state-changed';

/**
 * Flipped false after the first quota failure. Without it, an operator with a
 * full localStorage would throw-and-catch on every keystroke for the rest of
 * the session — state still lives in React, so only persistence degrades, and
 * it degrades with exactly one warning instead of thousands.
 */
let writable = true;

const isQuotaError = (e) =>
  e?.name === 'QuotaExceededError'
  || e?.name === 'NS_ERROR_DOM_QUOTA_REACHED'
  || e?.code === 22
  || e?.code === 1014;

export function readRaw(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private mode / storage disabled. Not an error worth surfacing.
    return null;
  }
}

/**
 * Evict the stalest grid-preference blobs to make room. Never touches the key
 * being written, and never touches anything outside the gridState_ namespace —
 * session data and bookmarks are not ours to discard.
 */
function pruneOldestGridState(exceptKey) {
  try {
    const entries = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (!k || k === exceptKey || !k.startsWith('gridState_')) continue;
      let savedAt = 0;
      try {
        savedAt = Date.parse(JSON.parse(window.localStorage.getItem(k))?.savedAt) || 0;
      } catch {
        savedAt = 0; // unparseable is stalest by definition
      }
      entries.push({ k, savedAt });
    }
    if (entries.length === 0) return false;
    entries.sort((a, b) => a.savedAt - b.savedAt);
    entries.slice(0, Math.max(1, Math.ceil(entries.length / 4)))
      .forEach(({ k }) => window.localStorage.removeItem(k));
    return true;
  } catch {
    return false;
  }
}

export function writeRaw(key, value) {
  if (!writable) return false;
  const attempt = () => {
    window.localStorage.setItem(key, value);
    window.dispatchEvent(new CustomEvent(PERSIST_CHANGE_EVENT, { detail: { key } }));
    return true;
  };
  try {
    return attempt();
  } catch (e) {
    if (isQuotaError(e) && pruneOldestGridState(key)) {
      try {
        return attempt();
      } catch {
        // fall through to the disable path
      }
    }
    writable = false;
    console.warn('[persist] write failed; preferences will not survive reload:', e?.message);
    return false;
  }
}

export function removeRaw(key) {
  try {
    window.localStorage.removeItem(key);
    window.dispatchEvent(new CustomEvent(PERSIST_CHANGE_EVENT, { detail: { key } }));
  } catch {
    // nothing useful to do
  }
}

/**
 * Read `{__v, savedAt, state}`.
 *
 * @param {string} key
 * @param {{version: number, migrate?: (from: number, state: any) => any,
 *          validate?: (state: any) => any}} opts
 *   `migrate` and `validate` both signal rejection by returning undefined.
 * @returns the stored state, or undefined if there is nothing usable.
 */
export function readVersioned(key, { version, migrate, validate } = {}) {
  const raw = readRaw(key);
  if (!raw) return undefined;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }

  const hasEnvelope = parsed && typeof parsed === 'object' && typeof parsed.__v === 'number';
  const found = hasEnvelope ? parsed.__v : 1;

  // Written by a NEWER build than this one — the user rolled back, or an old
  // tab is still open. Never guess at a shape from the future.
  if (found > version) return undefined;

  // No envelope means a legacy v1 blob, which is the bare value itself.
  let state = hasEnvelope ? parsed.state : parsed;

  if (found < version) {
    try {
      state = migrate ? migrate(found, state) : undefined;
    } catch {
      return undefined;
    }
  }
  if (state === undefined) return undefined;

  try {
    return validate ? validate(state) : state;
  } catch {
    return undefined;
  }
}

export function writeVersioned(key, state, { version } = {}) {
  return writeRaw(
    key,
    JSON.stringify({ __v: version, savedAt: new Date().toISOString(), state }),
  );
}

/**
 * Observe a key. Fires for same-tab writes (CustomEvent) and other-tab writes
 * (the native `storage` event, which never fires in the tab that wrote).
 */
export function subscribe(key, fn) {
  const onLocal = (e) => {
    if (!e?.detail?.key || e.detail.key === key) fn();
  };
  const onStorage = (e) => {
    if (!e.key || e.key === key) fn();
  };
  window.addEventListener(PERSIST_CHANGE_EVENT, onLocal);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(PERSIST_CHANGE_EVENT, onLocal);
    window.removeEventListener('storage', onStorage);
  };
}

/** Test seam — re-arms writing after a simulated quota failure. */
export function __resetPersistentStore() {
  writable = true;
}
